import { type Announcement, type ListAnnouncementsQuery } from '@clockwise/clients/announcements';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Filter, Plus, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AnnouncementCriteriaBadge } from '@/components/custom/announcement-criteria-badge';
import { AnnouncementFormSheet } from '@/components/custom/announcement-form-sheet';
import { NoAccess } from '@/components/custom/no-access';
import { PaginationFooter } from '@/components/custom/pagination-footer';
import { PillBadge } from '@/components/custom/pill-badge';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePageWindow } from '@/hooks/use-page-window';
import {
  ANNOUNCEMENT_STATUS_COLOR_CLASSES,
  ANNOUNCEMENT_STATUS_LABELS,
  announcementStatus,
} from '@/lib/announcement-status';
import { announcementsApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

type SortField = 'heading' | 'startAt' | 'endAt' | 'createdAt' | 'updatedAt';
type StatusFilter = 'active' | 'inactive' | 'all';

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'startAt';
const DEFAULT_SORT_DIRECTION = 'desc';

/**
 * The Announcements tab of the Guides and Updates page (previously its own
 * Setup screen — moved here so every signed-in user reaches it from the
 * account menu instead of Setup, which is admin-only navigation). Same
 * read/write permissions as before (`announcement.view` / `.edit`); only the
 * entry point moved.
 *
 * Filter/sort/page state is local (not URL search params) because this is a
 * tab-embedded panel, not its own route — same convention as
 * `CustomerTeamPanel`.
 */
export function AnnouncementsPanel() {
  const canView = useHasPermission('announcement.view');
  if (!canView) return <NoAccess permission="announcement.view" resource="Announcements" />;
  return <AnnouncementsPanelContent />;
}

function AnnouncementsPanelContent() {
  const queryClient = useQueryClient();

  const [sortField, setSortField] = useState<SortField>(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(DEFAULT_SORT_DIRECTION);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const q = useDebouncedValue(search.trim(), 300);

  useEffect(() => {
    setPage(0);
  }, [q, status]);

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const listQuery: ListAnnouncementsQuery = {
    q: q || undefined,
    active: status === 'all' ? undefined : status === 'active',
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.announcements.list(listQuery),
    queryFn: () => announcementsApi.list(listQuery),
    placeholderData: keepPreviousData,
    // This list changes only on an admin mutation, which already invalidates
    // the query directly — a short staleTime just avoids an unnecessary
    // refetch when switching back to this tab, not staleness after an edit.
    staleTime: 30_000,
  });

  const total = query.data?.meta.total ?? 0;
  const { pageCount, shownOffset } = usePageWindow({
    page,
    setPage,
    pageSize: PAGE_SIZE,
    total,
    responseOffset: query.data?.meta.offset,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementsApi.remove(id),
    onSuccess: () => {
      toast.success('Announcement deactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => announcementsApi.restore(id),
    onSuccess: () => {
      toast.success('Announcement reactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<Announcement>[]>(
    () => [
      {
        accessorKey: 'criteria',
        header: 'Criteria',
        enableSorting: false,
        cell: ({ row }) => <AnnouncementCriteriaBadge criteria={row.original.criteria} />,
      },
      {
        accessorKey: 'heading',
        header: 'Heading',
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.heading}
            {!row.original.active ? (
              <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const status = announcementStatus(row.original);
          return (
            <PillBadge
              variant="outline"
              className={`border-transparent ${ANNOUNCEMENT_STATUS_COLOR_CLASSES[status]}`}
            >
              {ANNOUNCEMENT_STATUS_LABELS[status]}
            </PillBadge>
          );
        },
      },
      {
        accessorKey: 'startAt',
        header: 'Starts',
        cell: ({ row }) => (
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            {new Date(row.original.startAt).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'endAt',
        header: 'Ends',
        cell: ({ row }) =>
          row.original.endAt ? (
            <span className="text-xs whitespace-nowrap text-muted-foreground">
              {new Date(row.original.endAt).toLocaleString()}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="announcement.edit">
            <RowActions>
              {row.original.active ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Deactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Deactivate "${row.original.heading}"?`)) {
                      deleteMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Reactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      setSortField(first.id as SortField);
      setSortDirection(first.desc ? 'desc' : 'asc');
      setPage(0);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search heading, subheading…"
          value={search}
          onValueChange={setSearch}
        />
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <RequirePermission permission="announcement.edit">
            <Button
              onClick={() => {
                setEditing(null);
                setSheetOpen(true);
              }}
            >
              <Plus />
              Add announcement
            </Button>
          </RequirePermission>
        </div>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load announcements.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q
                    ? 'No announcements match this search.'
                    : 'No announcements yet. Click “Add announcement”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() => {
                    setEditing(r.original);
                    setSheetOpen(true);
                  }}
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <PaginationFooter
        shownOffset={shownOffset}
        shownCount={query.data?.data.length ?? 0}
        total={total}
        pageSize={PAGE_SIZE}
        page={page}
        pageCount={pageCount}
        isFetching={query.isFetching}
        onPrevious={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      <AnnouncementFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        announcement={editing}
      />
    </div>
  );
}

const defaultCell: ColumnDef<Announcement>['cell'] = ({ getValue }) => String(getValue() ?? '');
